import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Spinner } from '../components/ui/Spinner';
import { ProtectedRoute } from '../features/auth/ProtectedRoute';
import { useAuth } from '../features/auth/useAuth';
import { AppShell } from './layout/AppShell';
import { NAV_ITEMS } from './navigation';

const AiAnalyticsPage = lazy(() => import('../pages/AiAnalyticsPage').then((m) => ({ default: m.AiAnalyticsPage })));
const AiAnalyticsSettingsPage = lazy(() => import('../pages/AiAnalyticsSettingsPage').then((m) => ({ default: m.AiAnalyticsSettingsPage })));
const AiDeficitDetailPage = lazy(() => import('../pages/AiDeficitDetailPage').then((m) => ({ default: m.AiDeficitDetailPage })));
const AnalyticsPage = lazy(() => import('../pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const BrandingSettingsPage = lazy(() => import('../pages/BrandingSettingsPage').then((m) => ({ default: m.BrandingSettingsPage })));
const CompetitorModelDetailPage = lazy(() => import('../pages/CompetitorModelDetailPage').then((m) => ({ default: m.CompetitorModelDetailPage })));
const DealerDetailPage = lazy(() => import('../pages/DealerDetailPage').then((m) => ({ default: m.DealerDetailPage })));
const DealerModelShipmentsPage = lazy(() => import('../pages/DealerModelShipmentsPage').then((m) => ({ default: m.DealerModelShipmentsPage })));
const DealerStockPage = lazy(() => import('../pages/DealerStockPage').then((m) => ({ default: m.DealerStockPage })));
const IntegrationDetailPage = lazy(() => import('../pages/IntegrationDetailPage').then((m) => ({ default: m.IntegrationDetailPage })));
const IntegrationsPage = lazy(() => import('../pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));
const InventoryPage = lazy(() => import('../pages/InventoryPage').then((m) => ({ default: m.InventoryPage })));
const LeadDetailPage = lazy(() => import('../pages/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })));
const LoginPage = lazy(() => import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterDealerPage = lazy(() => import('../pages/RegisterDealerPage').then((m) => ({ default: m.RegisterDealerPage })));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const LogisticsCatalogPage = lazy(() => import('../pages/LogisticsCatalogPage').then((m) => ({ default: m.LogisticsCatalogPage })));
const LogisticsHomologationPage = lazy(() => import('../pages/LogisticsHomologationPage').then((m) => ({ default: m.LogisticsHomologationPage })));
const LogisticsImportPage = lazy(() => import('../pages/LogisticsImportPage').then((m) => ({ default: m.LogisticsImportPage })));
const LogisticsPage = lazy(() => import('../pages/LogisticsPage').then((m) => ({ default: m.LogisticsPage })));
const LogisticsPositionPage = lazy(() => import('../pages/LogisticsPositionPage').then((m) => ({ default: m.LogisticsPositionPage })));
const MarketingHandlingPage = lazy(() => import('../pages/MarketingHandlingPage').then((m) => ({ default: m.MarketingHandlingPage })));
const MarketingPage = lazy(() => import('../pages/MarketingPage').then((m) => ({ default: m.MarketingPage })));
const NoAccessPage = lazy(() => import('../pages/NoAccessPage').then((m) => ({ default: m.NoAccessPage })));
const OverviewPage = lazy(() => import('../pages/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const ProductDetailPage = lazy(() => import('../pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })));
const QuotaAccessPage = lazy(() => import('../pages/QuotaAccessPage').then((m) => ({ default: m.QuotaAccessPage })));
const QuotaCatalogPage = lazy(() => import('../pages/QuotaCatalogPage').then((m) => ({ default: m.QuotaCatalogPage })));
const QuotaChinaPage = lazy(() => import('../pages/QuotaChinaPage').then((m) => ({ default: m.QuotaChinaPage })));
const QuotaDealerPage = lazy(() => import('../pages/QuotaDealerPage').then((m) => ({ default: m.QuotaDealerPage })));
const QuotaModelPage = lazy(() => import('../pages/QuotaModelPage').then((m) => ({ default: m.QuotaModelPage })));
const QuotasPage = lazy(() => import('../pages/QuotasPage').then((m) => ({ default: m.QuotasPage })));
const SaleDetailPage = lazy(() => import('../pages/SaleDetailPage').then((m) => ({ default: m.SaleDetailPage })));
const SalesPage = lazy(() => import('../pages/SalesPage').then((m) => ({ default: m.SalesPage })));
const SectionPlaceholderPage = lazy(() => import('../pages/SectionPlaceholderPage').then((m) => ({ default: m.SectionPlaceholderPage })));
const StockOptimizationDetailPage = lazy(() => import('../pages/StockOptimizationDetailPage').then((m) => ({ default: m.StockOptimizationDetailPage })));
const StockOptimizationPage = lazy(() => import('../pages/StockOptimizationPage').then((m) => ({ default: m.StockOptimizationPage })));
const TdPikInventoryPage = lazy(() => import('../pages/TdPikInventoryPage').then((m) => ({ default: m.TdPikInventoryPage })));
const TeamPage = lazy(() => import('../pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const ContactCenterPage = lazy(() => import('../pages/ContactCenterPage').then((m) => ({ default: m.ContactCenterPage })));
const CreditPage = lazy(() => import('../pages/CreditPage').then((m) => ({ default: m.CreditPage })));
const CreditClientFormPage = lazy(() => import('../pages/CreditClientFormPage').then((m) => ({ default: m.CreditClientFormPage })));

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] w-full items-center justify-center p-8">
      <div className="flex flex-col items-center gap-3">
        <Spinner size={32} />
        <span className="text-xs text-slate-400">Загрузка раздела...</span>
      </div>
    </div>
  );
}

/**
 * Корневой маршрут. «Обзор» — по праву `overview.view` (по умолчанию только у
 * директора/админа). Без этого права сотрудник попадает на первый доступный ему
 * раздел, а не на пустую страницу с ошибкой доступа.
 */
function HomeRoute() {
  const { user } = useAuth();
  const perms = user?.permissions;
  if (user && !perms?.overview?.view) {
    // Первый доступный пользователю раздел; если прав нет ни на один — понятный
    // экран «нет доступа», а не редирект на /sales (там был бы 403-тупик).
    const first = NAV_ITEMS.find((i) => i.path !== '/' && perms?.[i.module]?.view);
    return <Navigate to={first?.path ?? '/no-access'} replace />;
  }
  return <OverviewPage />;
}

// Разделы, у которых уже есть полноценная реализация (не заглушка)
const IMPLEMENTED = new Set([
  '/',
  '/sales',
  '/inventory',
  '/td-pik-inventory',
  '/stock-optimization',
  '/quotas',
  '/logistics',
  '/contact-center',
  '/credit',
  '/team',
  '/integrations',
  '/analytics',
  '/ai-analytics',
  '/marketing',
]);

export function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterDealerPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/credit-form/:token" element={<CreditClientFormPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<HomeRoute />} />
            <Route path="no-access" element={<NoAccessPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="sales/:id" element={<SaleDetailPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="inventory/:dealerId" element={<DealerStockPage />} />
            <Route path="td-pik-inventory" element={<TdPikInventoryPage />} />
            <Route path="stock-optimization" element={<StockOptimizationPage />} />
            <Route
              path="stock-optimization/:dealerId/:productId"
              element={<StockOptimizationDetailPage />}
            />
            <Route path="logistics" element={<LogisticsPage />} />
            <Route path="logistics/import" element={<LogisticsImportPage />} />
            <Route path="logistics/homologation" element={<LogisticsHomologationPage />} />
            <Route path="logistics/catalog" element={<LogisticsCatalogPage />} />
            <Route path="logistics/positions/:id" element={<LogisticsPositionPage />} />
            <Route path="contact-center" element={<ContactCenterPage />} />
            <Route path="credit" element={<CreditPage />} />
            <Route path="quotas" element={<QuotasPage />} />
            <Route path="quotas/catalog" element={<QuotaCatalogPage />} />
            <Route path="quotas/china" element={<QuotaChinaPage />} />
            <Route path="quotas/access" element={<QuotaAccessPage />} />
            <Route path="quotas/dealer/:dealerId" element={<QuotaDealerPage />} />
            <Route path="quotas/model/:productId" element={<QuotaModelPage />} />
            <Route path="dealers/:id" element={<DealerDetailPage />} />
            <Route
              path="dealers/:dealerId/shipments/:productId"
              element={<DealerModelShipmentsPage />}
            />
            <Route path="products/:id" element={<ProductDetailPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="integrations/:provider" element={<IntegrationDetailPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="analytics/models/:id" element={<CompetitorModelDetailPage />} />
            <Route path="analytics/competitors/models/:id" element={<CompetitorModelDetailPage />} />
            <Route path="ai-analytics" element={<AiAnalyticsPage />} />
            <Route path="ai-analytics/deficit" element={<AiDeficitDetailPage />} />
            <Route path="ai-analytics/settings" element={<AiAnalyticsSettingsPage />} />
            <Route path="marketing" element={<MarketingPage />} />
            <Route path="marketing/handling" element={<MarketingHandlingPage />} />
            <Route path="marketing/leads/:id" element={<LeadDetailPage />} />
            <Route path="settings/branding" element={<BrandingSettingsPage />} />
            {NAV_ITEMS.filter((item) => !IMPLEMENTED.has(item.path)).map((item) => (
              <Route key={item.path} path={item.path.slice(1)} element={<SectionPlaceholderPage />} />
            ))}
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
